// Password Reset API Service
// Handles all password reset related API calls

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  password_confirmation: string;
}

export interface PasswordResetResponse {
  message: string;
  status: 'success' | 'error';
  errors?: Record<string, string[]>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// Password Reset API class
class PasswordResetApi {
  /**
   * Request a password reset email
   */
  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/password/forgot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email } as ForgotPasswordRequest),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send password reset email');
      }

      return {
        message: data.message || 'Password reset instructions have been sent to your email.',
        status: 'success',
      };
    } catch (error) {
      return {
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        status: 'error',
        errors: error instanceof Error ? { general: [error.message] } : undefined,
      };
    }
  }

  /**
   * Reset password using token
   */
  async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          token,
          password,
          password_confirmation: passwordConfirmation,
        } as ResetPasswordRequest),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      return {
        message: data.message || 'Password has been reset successfully.',
        status: 'success',
      };
    } catch (error) {
      return {
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        status: 'error',
        errors: error instanceof Error ? { general: [error.message] } : undefined,
      };
    }
  }

  /**
   * Validate reset token
   */
  async validateResetToken(token: string): Promise<{ valid: boolean; message?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/password/validate/${token}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          valid: false,
          message: data.message || 'Invalid or expired reset token',
        };
      }

      return {
        valid: true,
        message: data.message || 'Token is valid',
      };
    } catch (error) {
      return {
        valid: false,
        message: error instanceof Error ? error.message : 'Failed to validate token',
      };
    }
  }
}

// Export singleton instance
export const passwordResetApi = new PasswordResetApi();

// Export types for use in components
export type { PasswordResetApi };
